// Mirayama hero: a relay of films across three tiles. Below 750px the grid is not shown at all
// (mm-hero-phone takes over), so the clock stops there and nothing is scheduled or downloaded.
//
// Resting tiles always show studio photographs, drifting very slowly (CSS). The baton goes
// left → centre → right → left… and the films go round in block order (Gatsby, Evergreen, Nomad,
// Mazestroom, Gatsby…): four films over three tiles, so each lands in a different tile every round.
// A film starts playing hidden under the tile's photo and dissolves in (0.9 s) only once it is advancing.
// During its last 1.1 s of motion it dissolves out to the tile's next photo (no hold on the last
// frame); a beat after that dissolve begins, the next tile's film is cued, so it is dissolving in
// while this one is still moving under its photograph: one figure is always in motion, and the two
// changes are staggered rather than simultaneous.
// Never the same piece twice on screen; labels and links change with their images.
// Each film is downloaded once and replayed from memory (a blob), so the loop costs no more data.
// Reduced motion or data saver: no films and no drift; the tiles dissolve between photos, one at a time.
// A Pause/Play button stops everything; off screen or in a background tab it waits.
if (!customElements.get('mm-hero-grid')) {
  const FADE = 800; // ms, photo to photo
  const FILM_IN = 900; // ms, photo to film
  const FILM_OUT = 1100; // ms, film to photo: the movement settles into the photograph
  const MIN_SEEN = 400; // ms, a film is fully in view at least this long (short films are slowed to allow it)
  const CUE = 150; // ms, the next film starts (hidden) this long after the current one starts to fade out
  const ADVANCED = 0.05; // s, a film counts as moving once past this point
  const WAIT_FOR_CACHE = 2500; // ms, a cue waits this long for the film's download before streaming it
  const PATIENCE = 4000; // ms, a film that will not start within this is skipped
  const TICK = 40;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  customElements.define(
    'mm-hero-grid',
    class MmHeroGrid extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        let data;
        try {
          data = JSON.parse(this.querySelector('.mm-hgrid__data').textContent);
        } catch (e) {
          return;
        }
        this.pieces = data.pieces || [];
        this.sizes = data.sizes;
        this.holdMs = (parseFloat(this.dataset.hold) || 6) * 1000;
        this.speed = Math.min(1, Math.max(0.5, (parseFloat(this.dataset.speed) || 100) / 100));
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const saveData = Boolean(navigator.connection && navigator.connection.saveData);
        this.useFilms = !reduceMotion && !saveData;

        this.byIndex = new Map(this.pieces.map((p) => [p.index, p]));
        this.films = this.pieces.filter((p) => p.film);
        this.cache = new Map();
        this.stillOnly = this.pieces.filter((p) => !p.film);
        this.stillTurn = new Map(this.pieces.map((p) => [p.index, 0]));
        this.tiles = Array.from(this.querySelectorAll('.mm-hgrid__tile')).map((el) => {
          const layers = Array.from(el.querySelectorAll('.mm-hgrid__slide'));
          const labels = Array.from(el.querySelectorAll('.mm-hgrid__label'));
          const active = layers.find((l) => l.classList.contains('is-active')) || layers[layers.length - 1];
          const piece = active.dataset.piece === '' ? null : parseInt(active.dataset.piece, 10);
          if (piece !== null) this.stillTurn.set(piece, 1);
          if (piece !== null && !reduceMotion) this.drift(active);
          return { el, role: el.dataset.role, layers, labels, active, piece, fading: false, leaving: null, lastStill: piece };
        });

        this.toggle = this.closest('.mm-hgrid').querySelector('.mm-hgrid__toggle');
        if (this.pieces.length < 2) return;

        this.now = 0;
        this.events = [];
        this.paused = false;
        this.visible = true;
        if (this.toggle) {
          this.toggle.hidden = false;
          this.toggle.addEventListener('click', () => this.setPaused(!this.paused));
        }
        new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.updateFilms();
        }).observe(this);
        document.addEventListener('visibilitychange', () => this.updateFilms());
        // The grid is display:none below 750px: the clock runs only while it can be seen.
        this.desktop = window.matchMedia('(min-width: 750px)');
        this.desktop.addEventListener('change', () => this.setClock(this.desktop.matches));

        const begin = () => this.setClock(this.desktop.matches);
        if (document.readyState === 'complete') {
          begin();
        } else {
          window.addEventListener('load', begin, { once: true });
        }
      }

      disconnectedCallback() {
        this.setClock(false);
        // Let go of the films held in memory.
        this.cache.forEach((entry) => entry.url && URL.revokeObjectURL(entry.url));
        this.cache.clear();
      }

      // Start the clock (and the relay) when the grid comes into use; stop it when the grid is hidden.
      // Scheduled events keep their place and carry on from a restart.
      setClock(on) {
        if (on === Boolean(this.timer)) return;
        if (on) {
          this.last = performance.now();
          this.timer = setInterval(() => this.tick(), TICK);
          this.restart();
        } else {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      // ---- clock: running time only (not paused, on screen, tab visible) ----

      get running() {
        return !this.paused && this.visible && !document.hidden;
      }

      tick() {
        const t = performance.now();
        const dt = Math.min(250, t - this.last);
        this.last = t;
        if (!this.running) return;
        this.now += dt;
        for (;;) {
          this.events.sort((a, b) => a.at - b.at);
          const next = this.events[0];
          if (!next || next.at > this.now) break;
          this.events.shift();
          if (next.gen === this.gen) next.fn();
        }
      }

      at(time, fn) {
        this.events.push({ at: time, fn, gen: this.gen });
      }

      every(fn) {
        // Calls fn each tick until it returns true.
        const loop = () => {
          if (!fn()) this.at(this.now + TICK, loop);
        };
        this.at(this.now, loop);
      }

      setPaused(paused) {
        this.paused = paused;
        this.toggle.textContent = paused ? 'Play' : 'Pause';
        this.toggle.setAttribute('aria-label', paused ? 'Play the moving images' : 'Pause the moving images');
        this.classList.toggle('is-paused', paused);
        this.updateFilms();
      }

      // Films (showing or cueing) play only while running.
      updateFilms() {
        const run = this.running;
        this.classList.toggle('is-paused', !run);
        this.tiles.forEach((tile) => {
          tile.layers.forEach((layer) => {
            const video = layer.querySelector('video');
            if (!video || !layer.dataset.playing || video.ended) return;
            if (run && video.paused) {
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
            } else if (!run && !video.paused) {
              video.pause();
            }
          });
        });
      }

      visibleTiles() {
        return this.tiles.filter((t) => t.el.offsetParent !== null);
      }

      // ---- start (and restart when the number of tiles changes) ----

      restart() {
        this.gen = (this.gen || 0) + 1;
        this.events = [];
        this.relay = this.visibleTiles();
        if (!this.relay.length) return;
        this.tiles.forEach((tile) => tile.layers.forEach((l) => l.classList.contains('is-cueing') && this.release(l)));
        if (!this.useFilms || !this.films.length) {
          this.stillCycle(0);
          return;
        }
        this.k = this.k || 0;
        this.preload(this.k);
        this.preload(this.k + 1);
        this.cue(this.k, this.now + 300);
      }

      filmOf(k) {
        const n = this.films.length;
        return this.films[((k % n) + n) % n];
      }

      // ---- film cache: each film is fetched once and replayed from memory ----

      cacheFilm(piece) {
        let entry = this.cache.get(piece.index);
        if (entry) return entry;
        entry = { url: null, failed: false };
        this.cache.set(piece.index, entry);
        fetch(piece.film.sd || piece.film.hd)
          .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(r.status))))
          .then((blob) => {
            entry.url = URL.createObjectURL(blob);
          })
          .catch(() => {
            entry.failed = true;
          });
        return entry;
      }

      filmReady(piece) {
        const entry = this.cacheFilm(piece);
        return Boolean(entry.url) || entry.failed;
      }

      filmSrc(piece) {
        const entry = this.cache.get(piece.index);
        return entry && entry.url ? entry.url : piece.film.sd || piece.film.hd;
      }

      tileOf(k) {
        return this.relay[k % this.relay.length];
      }

      // Natural speed, unless the film is too short to be seen fully between its two dissolves.
      rate(piece) {
        const length = (piece.film.duration || 3000) / this.speed;
        const needed = FILM_IN + FILM_OUT + MIN_SEEN;
        return Math.min(this.speed, (this.speed * length) / Math.max(length, needed));
      }

      // ---- the relay ----

      // Load baton k's film into its tile's hidden layer (one turn ahead).
      preload(k) {
        const tile = this.tileOf(k);
        const piece = this.filmOf(k);
        this.every(() => {
          if (tile.fading || !this.filmReady(piece)) return false;
          const layer = this.spare(tile);
          if (layer.dataset.film !== String(piece.index) || layer.dataset.src !== this.filmSrc(piece)) this.setFilm(tile, layer, piece);
          return true;
        });
      }

      setFilm(tile, layer, piece) {
        this.release(layer);
        layer.dataset.film = piece.index;
        const video = layer.querySelector('video');
        video.muted = true;
        video.loop = false;
        layer.dataset.src = this.filmSrc(piece);
        video.src = layer.dataset.src;
        video.preload = 'auto';
        video.load();
      }

      // Start baton k's film hidden under the tile's photo; dissolve it in once it is moving.
      cue(k, due) {
        const tile = this.tileOf(k);
        const piece = this.filmOf(k);
        let started = null;
        let waiting = null;
        this.at(due, () =>
          this.every(() => {
            if (tile.fading) return false;
            if (started === null && !this.filmReady(piece)) {
              if (waiting === null) waiting = this.now;
              if (this.now - waiting < WAIT_FOR_CACHE) return false;
            }
            const elsewhere = this.relay.some((t) => t !== tile && (t.piece === piece.index || t.leaving === piece.index));
            if (elsewhere) return false;
            const layer = this.spare(tile);
            if (layer.dataset.film !== String(piece.index)) this.setFilm(tile, layer, piece);
            const video = layer.querySelector('video');
            if (started === null) {
              started = this.now;
              layer.classList.add('is-cueing');
              layer.dataset.playing = '1';
              video.playbackRate = this.rate(piece);
              video.defaultPlaybackRate = video.playbackRate;
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
              return false;
            }
            if (video.currentTime > ADVANCED && !video.paused) {
              this.filmIn(k, tile, layer, piece);
              return true;
            }
            if (this.now - started > PATIENCE) {
              // It will not start: skip it; the tile keeps its photo, the baton moves on.
              this.release(layer);
              this.cue(k + 1, this.now);
              return true;
            }
            return false;
          })
        );
      }

      filmIn(k, tile, layer, piece) {
        this.k = k + 1; // a restart carries on from the next film
        layer.classList.remove('is-cueing');
        this.show(tile, layer, piece, 'film', FILM_IN);
        this.preload(k + 1);
        const video = layer.querySelector('video');
        // Choose and decode the photo this tile rests on next now, so the dissolve out is on time.
        tile.nextStill = this.pickStill(tile, k);
        tile.nextImage = this.stillImage(tile.nextStill);
        const pre = new Image();
        pre.sizes = this.sizes;
        pre.srcset = tile.nextImage.srcset;
        pre.src = tile.nextImage.src;
        if (pre.decode) pre.decode().catch(() => {});
        this.every(() => {
          const rate = video.playbackRate || 1;
          const left = ((video.duration || piece.film.duration / 1000) - video.currentTime) / rate;
          if (left * 1000 <= FILM_OUT + 2 * TICK || video.ended) {
            this.filmOut(k, tile);
            this.cue(k + 1, this.now + CUE);
            return true;
          }
          return false;
        });
      }

      // During the film's last moments of motion: dissolve to the tile's next photo.
      filmOut(k, tile) {
        let piece = tile.nextStill;
        const busy = this.relay.some((t) => t !== tile && [t.piece, t.leaving, t.reserved].includes(piece.index));
        let image = tile.nextImage;
        if (busy) {
          piece = this.pickStill(tile, k);
          image = this.stillImage(piece);
        }
        this.every(() => {
          if (tile.fading) return false;
          this.toStill(tile, piece, FILM_OUT, image);
          return true;
        });
      }

      // A photo for a resting tile: a piece without a film first (not on screen, not this tile's last
      // photo); failing that, a photo of the piece this tile will play next; failing that, any free piece.
      pickStill(tile, k) {
        const upcoming = this.filmOf(k + this.relay.length);
        const busy = new Set();
        this.relay.forEach((t) => [t.piece, t.leaving, t.reserved].forEach((p) => p != null && busy.add(p)));
        // Photos other tiles have already chosen for their next rest.
        this.relay.forEach((t) => t !== tile && t.nextStill && busy.add(t.nextStill.index));
        // The films about to arrive in the other tiles are on screen soon: keep them out too.
        for (let j = 1; j < this.relay.length; j += 1) busy.add(this.filmOf(k + j).index);
        const free = (p) => !busy.has(p.index);
        this.stillCursor = this.stillCursor || 0;
        const n = this.stillOnly.length;
        const rotated = this.stillOnly.slice(this.stillCursor % (n || 1)).concat(this.stillOnly.slice(0, this.stillCursor % (n || 1)));
        let pick = rotated.find((p) => free(p) && p.index !== tile.lastStill);
        if (pick) this.stillCursor = this.stillOnly.indexOf(pick) + 1;
        if (!pick && upcoming && free(upcoming)) pick = upcoming;
        if (!pick) pick = rotated.find(free);
        if (!pick) pick = this.pieces.find(free) || this.stillOnly[0] || this.pieces[0];
        return pick;
      }

      // ---- reduced motion / data saver: photos only, one tile at a time ----

      stillCycle(turn) {
        const tiles = this.relay;
        const step = Math.max(1500, this.holdMs / tiles.length);
        this.at(this.now + step, () => {
          const tile = tiles[turn % tiles.length];
          const busy = new Set(this.relay.flatMap((t) => [t.piece, t.leaving, t.reserved]));
          const pool = this.pieces;
          this.stillCursor = this.stillCursor || 0;
          for (let i = 0; i < pool.length; i += 1) {
            const piece = pool[(this.stillCursor + i) % pool.length];
            if (!busy.has(piece.index)) {
              this.stillCursor = (this.stillCursor + i + 1) % pool.length;
              if (!tile.fading) this.toStill(tile, piece, FADE);
              break;
            }
          }
          this.stillCycle(turn + 1);
        });
      }

      // ---- layers ----

      spare(tile) {
        return tile.layers.find((l) => l !== tile.active);
      }

      // Stop and empty a layer's film.
      release(layer) {
        layer.classList.remove('is-cueing');
        delete layer.dataset.playing;
        delete layer.dataset.film;
        delete layer.dataset.src;
        const video = layer.querySelector('video');
        if (!video || !video.getAttribute('src')) return;
        video.pause();
        video.removeAttribute('src');
        video.preload = 'none';
        video.load();
      }

      // The next photo of a piece (each piece's photos take turns).
      stillImage(piece) {
        const turn = this.stillTurn.get(piece.index) || 0;
        this.stillTurn.set(piece.index, turn + 1);
        return piece.stills[turn % piece.stills.length];
      }

      async toStill(tile, piece, fade, chosen) {
        tile.reserved = piece.index;
        tile.lastStill = piece.index;
        tile.nextStill = null;
        tile.fading = true;
        const layer = this.spare(tile);
        this.release(layer);
        const image = chosen || this.stillImage(piece);
        const img = layer.querySelector('img');
        img.loading = 'eager';
        img.sizes = this.sizes;
        img.srcset = image.srcset;
        img.src = image.src;
        img.alt = image.alt || piece.title;
        await Promise.race([img.decode ? img.decode().catch(() => {}) : null, sleep(chosen ? 150 : 1500)]);
        this.drift(layer);
        tile.fading = false;
        this.show(tile, layer, piece, 'still', fade);
      }

      // Restart the slow drift on a photo.
      drift(layer) {
        layer.classList.remove('is-drifting');
        void layer.offsetWidth; // eslint-disable-line no-void
        layer.classList.add('is-drifting');
      }

      // Dissolve the tile to a prepared layer: it (and its label) fades in over the old one, which stays
      // in place beneath it; the old layer is let go afterwards.
      async show(tile, layer, piece, mode, fade) {
        tile.fading = true;
        tile.el.style.setProperty('--mm-fade', `${fade}ms`);
        layer.dataset.piece = piece.index;
        layer.dataset.mode = mode;
        layer.href = piece.url;
        // The link's name: a film layer has no image text, so the piece's title names it.
        layer.setAttribute('aria-label', piece.title || '');
        layer.style.setProperty('--mm-film-zoom', piece.film ? piece.film.zoom : 1);
        layer.style.setProperty('--mm-film-shift', `${piece.film ? piece.film.shift : 0}%`);
        layer.style.setProperty('--mm-still-zoom', piece.stillZoom || 1);
        layer.style.setProperty('--mm-still-shift', `${piece.stillShift || 0}%`);
        const label = tile.labels[tile.layers.indexOf(layer)];
        label.querySelector('.mm-tech-label__num').textContent = piece.number || '';
        label.querySelector('.mm-tech-label__name').textContent = piece.title || '';
        label.querySelector('.mm-tech-label__meta').textContent = piece.colour || '';
        const old = tile.active;
        tile.leaving = tile.piece;
        old.classList.add('is-leaving');
        old.classList.remove('is-active');
        old.setAttribute('aria-hidden', 'true');
        old.tabIndex = -1;
        layer.classList.add('is-active');
        layer.setAttribute('aria-hidden', 'false');
        layer.removeAttribute('tabindex');
        tile.labels[tile.layers.indexOf(old)].classList.remove('is-active');
        label.classList.add('is-active');
        tile.active = layer;
        tile.piece = piece.index;
        tile.reserved = null;
        await sleep(fade + 60);
        old.classList.remove('is-leaving', 'is-drifting');
        if (old.dataset.mode === 'film') this.release(old);
        tile.leaving = null;
        tile.fading = false;
      }
    }
  );
}
